import { zeroErrorMessage } from "@/lib/zero-error";
import { useQuery as useZeroQuery, useZero } from "@rocicorp/zero/react";
import { appMutators, appQueries } from "@production-hub/shared";
import { useState } from "react";
import { toast } from "sonner";
import { toFriendlyMutationError } from "@/lib/error-utils";
import { generateUUID } from "@/lib/utils";
import type { Equipment } from "@/hooks/useEquipment";

export type EquipmentContent = {
  id: string;
  parent_equipment_id: string;
  /** Stock article, or null for a free line */
  child_equipment_id: string | null;
  /** Name of a free line, or null for a stock article */
  name: string | null;
  quantity: number;
  created_at: string | null;
  updated_at: string | null;
  child_equipment?: Equipment | null;
};

type ZeroMutationResult =
  | { type: "error"; error: { message: string } }
  | { type: string; error?: never };
type ZeroMutateClient = {
  mutate: (mutation: unknown) => {
    client: Promise<void>;
    server: Promise<ZeroMutationResult>;
  };
};
type MutationOptions<T> = { onSuccess?: (data: T) => void };

type ZeroEquipmentContentRow = {
  id: string;
  parentEquipmentID: string;
  childEquipmentID?: string | null;
  name?: string | null;
  quantity: number;
  createdAt?: number | string | null;
  updatedAt?: number | string | null;
  childEquipment?: {
    id: string;
    number?: string | null;
    name: string;
    manufacturer?: string | null;
    isSerialized: boolean;
    stockQuantity?: number | null;
    shortName?: string | null;
  } | null;
};

function toLegacyTimestamp(value: number | string | null | undefined) {
  if (typeof value === "number") return new Date(value).toISOString();
  return value ?? null;
}

function toLegacyChildEquipment(
  row: ZeroEquipmentContentRow["childEquipment"],
): Equipment | null {
  if (!row) return null;
  return {
    id: row.id,
    number: row.number ?? null,
    name: row.name,
    manufacturer: row.manufacturer ?? null,
    icon: null,
    new_price: null,
    rental_price: null,
    is_serialized: row.isSerialized,
    stock_quantity: row.stockQuantity ?? null,
    country_of_origin: null,
    customs_tariff_number: null,
    carnet_ata_description: null,
    carnet_ata_value: null,
    carnet_ata_weight_unit: null,
    notes: null,
    parent_equipment_id: null,
    created_at: null,
    updated_at: null,
    deleted_at: null,
    version: null,
    rentman_id: null,
    weight: null,
    width: null,
    height: null,
    length: null,
    volume: null,
    power: null,
    current_draw: null,
    qr_code: null,
    external_remark: null,
    location_in_warehouse: null,
    short_name: row.shortName ?? null,
    tags: [],
    images: [],
  };
}

function toLegacyContent(row: ZeroEquipmentContentRow): EquipmentContent {
  return {
    id: row.id,
    parent_equipment_id: row.parentEquipmentID,
    child_equipment_id: row.childEquipmentID ?? null,
    name: row.name ?? null,
    quantity: row.quantity,
    created_at: toLegacyTimestamp(row.createdAt),
    updated_at: toLegacyTimestamp(row.updatedAt),
    child_equipment: toLegacyChildEquipment(row.childEquipment),
  };
}

async function reportZeroMutationFailure(
  serverResult: Promise<ZeroMutationResult>,
  fallback: string,
) {
  const result = await serverResult;
  if (result.type === "error") {
    throw new Error(result.error?.message || fallback);
  }
}

function useZeroMutation<TInput, TResult>({
  run,
  success,
  failure,
}: {
  run: (input: TInput) => Promise<TResult>;
  success: string;
  failure: string;
}) {
  const [isPending, setIsPending] = useState(false);
  const wrappedRun = async (input: TInput) => {
    setIsPending(true);
    try {
      return await run(input);
    } catch (error) {
      throw toFriendlyMutationError(error);
    } finally {
      setIsPending(false);
    }
  };
  return {
    mutate: (input: TInput, options?: MutationOptions<TResult>) => {
      void wrappedRun(input)
        .then((data) => {
          toast.success(success);
          options?.onSuccess?.(data);
        })
        .catch((error) => toast.error(`${failure}: ${error.message}`));
    },
    mutateAsync: wrappedRun,
    isPending,
  };
}

export const useEquipmentContents = (equipmentId: string | undefined) => {
  const query = equipmentId
    ? appQueries.equipmentContents.byParent({ parentEquipmentID: equipmentId })
    : appQueries.equipmentContents.byParent({
        parentEquipmentID: "00000000-0000-0000-0000-000000000000",
      });
  const [data, details] = useZeroQuery(query, { enabled: !!equipmentId });
  const contents = ((data ?? []) as ZeroEquipmentContentRow[]).map(
    toLegacyContent,
  );

  return {
    data: contents,
    isLoading:
      !!equipmentId && details.type !== "complete" && contents.length === 0,
    isFetching: !!equipmentId && details.type !== "complete",
    isError: details.type === "error",
    error:
      details.type === "error"
        ? new Error(zeroErrorMessage(details.error))
        : null,
  };
};

export const useAddEquipmentContent = () => {
  const zero = useZero() as ZeroMutateClient;
  return useZeroMutation<
    {
      parentEquipmentId: string;
      quantity?: number;
    } & (
      | { childEquipmentId: string; name?: never }
      | { name: string; childEquipmentId?: never }
    ),
    { id: string }
  >({
    success: "Content added",
    failure: "Failed to add content",
    run: async ({ parentEquipmentId, childEquipmentId, name, quantity }) => {
      const id = generateUUID();
      const mutation = zero.mutate(
        appMutators.equipmentContents.add({
          id,
          parentEquipmentID: parentEquipmentId,
          ...(childEquipmentId
            ? { childEquipmentID: childEquipmentId }
            : { name: name?.trim() }),
          quantity,
        }),
      );
      await mutation.client;
      await reportZeroMutationFailure(
        mutation.server,
        "Equipment content add rejected",
      );
      return { id };
    },
  });
};

export const useUpdateEquipmentContentQuantity = () => {
  const zero = useZero() as ZeroMutateClient;
  return useZeroMutation<
    { id: string; parentEquipmentId: string; quantity: number },
    { id: string; parentEquipmentId: string }
  >({
    success: "Quantity updated",
    failure: "Failed to update quantity",
    run: async ({ id, parentEquipmentId, quantity }) => {
      const mutation = zero.mutate(
        appMutators.equipmentContents.updateQuantity({ id, quantity }),
      );
      await mutation.client;
      await reportZeroMutationFailure(
        mutation.server,
        "Equipment content quantity update rejected",
      );
      return { id, parentEquipmentId };
    },
  });
};

export const useUpdateEquipmentContentName = () => {
  const zero = useZero() as ZeroMutateClient;
  return useZeroMutation<
    { id: string; parentEquipmentId: string; name: string },
    { id: string; parentEquipmentId: string }
  >({
    success: "Content renamed",
    failure: "Failed to rename content",
    run: async ({ id, parentEquipmentId, name }) => {
      const mutation = zero.mutate(
        appMutators.equipmentContents.updateName({ id, name: name.trim() }),
      );
      await mutation.client;
      await reportZeroMutationFailure(
        mutation.server,
        "Equipment content rename rejected",
      );
      return { id, parentEquipmentId };
    },
  });
};

export const useRemoveEquipmentContent = () => {
  const zero = useZero() as ZeroMutateClient;
  return useZeroMutation<
    { id: string; parentEquipmentId: string },
    { parentEquipmentId: string }
  >({
    success: "Content removed",
    failure: "Failed to remove content",
    run: async ({ id, parentEquipmentId }) => {
      const mutation = zero.mutate(
        appMutators.equipmentContents.remove({ id }),
      );
      await mutation.client;
      await reportZeroMutationFailure(
        mutation.server,
        "Equipment content remove rejected",
      );
      return { parentEquipmentId };
    },
  });
};
